import { Routes } from '@angular/router'
import { isAuthenticatedGuard } from './core/auth/is-authenticated.guard'
import { UnauthenticatedComponent } from './core/auth/unauthenticated.component'
import type { PageBreadcrumbItem } from './shared/utils/page-breadcrumbs'

const permissionsPageComponent = () =>
  import('./features/permissions/permissions-page.component').then((m) => m.PermissionsPageComponent)

const tokensPageComponent = () => import('./features/tokens/tokens-page.component').then((m) => m.TokensPageComponent)

const domainsPageComponent = () =>
  import('./features/domains/domains-page.component').then((m) => m.DomainsPageComponent)

const domainDetailPageComponent = () =>
  import('./features/domains/domain-detail-page.component').then((m) => m.DomainDetailPageComponent)

const projectDetailPageComponent = () =>
  import('./features/domains/project-detail-page.component').then((m) => m.ProjectDetailPageComponent)

const groupDetailPageComponent = () =>
  import('./features/domains/group-detail-page.component').then((m) => m.GroupDetailPageComponent)

const roleDetailPageComponent = () =>
  import('./features/domains/role-detail-page.component').then((m) => m.RoleDetailPageComponent)

const policyDetailPageComponent = () =>
  import('./features/domains/policy-detail-page.component').then((m) => m.PolicyDetailPageComponent)

const usersPageComponent = () => import('./features/users/users-page.component').then((m) => m.UsersPageComponent)

const projectsPageComponent = () =>
  import('./features/projects/projects-page.component').then((m) => m.ProjectsPageComponent)

const userDetailPageComponent = () =>
  import('./features/users/user-detail-page.component').then((m) => m.UserDetailPageComponent)

const breadcrumbs = (...items: PageBreadcrumbItem[]) => ({
  breadcrumbs: items,
})

const topLevelRoutes: Routes = [
  {
    path: 'permissions',
    loadComponent: permissionsPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Permissions',
    data: breadcrumbs({ labelKey: 'NAVIGATION.PERMISSIONS' }),
  },
  {
    path: 'tokens',
    loadComponent: tokensPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'API Tokens',
    data: breadcrumbs({ labelKey: 'NAVIGATION.TOKENS' }),
  },
  {
    path: 'users',
    loadComponent: usersPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Users',
    data: breadcrumbs({ labelKey: 'NAVIGATION.USERS' }),
  },
  {
    path: 'projects',
    loadComponent: projectsPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Projects',
    data: breadcrumbs({ labelKey: 'NAVIGATION.PROJECTS' }),
  },
  {
    path: 'domains',
    loadComponent: domainsPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Domains',
    data: breadcrumbs({ labelKey: 'NAVIGATION.DOMAINS' }),
  },
]

export const routes: Routes = [
  {
    path: 'unauthenticated',
    component: UnauthenticatedComponent,
  },
  {
    path: '',
    loadComponent: () => import('./features/start/start.component').then((m) => m.StartComponent),
    canActivate: [isAuthenticatedGuard],
    title: 'Home',
    data: breadcrumbs({ labelKey: 'NAVIGATION.HOMEPAGE' }),
  },
  ...topLevelRoutes,
  {
    path: 'domains/domain/:domainId',
    loadComponent: domainDetailPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Domain',
    data: breadcrumbs(
      { labelKey: 'NAVIGATION.DOMAINS', link: '/domains' },
      { labelKey: 'DOMAIN_DETAIL_PAGE.BREADCRUMB_LABEL' },
    ),
  },
  {
    path: 'domains/project/:projectId',
    loadComponent: projectDetailPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Project',
    data: breadcrumbs(
      { labelKey: 'NAVIGATION.DOMAINS', link: '/domains' },
      { labelKey: 'PROJECT_DETAIL_PAGE.BREADCRUMB_LABEL' },
    ),
  },
  {
    path: 'domains/project/:projectId/groups/:groupId',
    loadComponent: groupDetailPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Group',
    data: breadcrumbs(
      { labelKey: 'NAVIGATION.DOMAINS', link: '/domains' },
      { labelKey: 'GROUP_DETAIL_PAGE.BREADCRUMB_LABEL' },
    ),
  },
  {
    path: 'domains/project/:projectId/roles/:roleId',
    loadComponent: roleDetailPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Role',
    data: breadcrumbs(
      { labelKey: 'NAVIGATION.DOMAINS', link: '/domains' },
      { labelKey: 'ROLE_DETAIL_PAGE.BREADCRUMB_LABEL' },
    ),
  },
  {
    path: 'domains/project/:projectId/policies/:policyId',
    loadComponent: policyDetailPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'Policy',
    data: breadcrumbs(
      { labelKey: 'NAVIGATION.DOMAINS', link: '/domains' },
      { labelKey: 'POLICY_DETAIL_PAGE.BREADCRUMB_LABEL' },
    ),
  },
  {
    path: 'users/:gid',
    loadComponent: userDetailPageComponent,
    canActivate: [isAuthenticatedGuard],
    title: 'User',
    data: breadcrumbs(
      { labelKey: 'NAVIGATION.USERS', link: '/users' },
      { labelKey: 'USER_DETAIL_PAGE.BREADCRUMB_LABEL' },
    ),
  },
  {
    path: '**',
    redirectTo: '',
  },
]
